import { useEffect, useState } from "react";
import { useLocation, Navigate, useNavigate } from "react-router-dom";
import useForm from "../../../hooks/useForm";
import Input from "../../../components/form/Input";
import Button from "../../../components/form/Button";
import useMutation from "../../../hooks/useMutation";
import { placeOrder } from "../../../services/cartService";
import PaymentPage from "./PaymentPage";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { resetStep } from "../../../redux/slice/stepSlice";
import { setCoverImages, setImages } from "../../../redux/slice/sketchSlice";
import { saveStep } from "../../../services/stepService";

// Address Suggestion Dropdown Component
const AddressSuggestionDropdown = ({ suggestion, originalAddress, onSelect, onDismiss }) => {
  if (!suggestion) return null;

  const formatSuggestedAddress = (suggested) => {
    return `${suggested.address_line1}${suggested.address_line2 ? ', ' + suggested.address_line2 : ''}, ${suggested.city_locality}, ${suggested.state_province} ${suggested.postal_code}`;
  };

  const formatOriginalAddress = (original) => {
    return `${original.address_line1}${original.address_line2 ? ', ' + original.address_line2 : ''}, ${original.city_locality}, ${original.state_province} ${original.postal_code}`;
  };

  return (
    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
      <div className="p-3 border-b border-gray-200 bg-yellow-50">
        <p className="text-sm text-yellow-800 font-medium">Address Validation Warning</p>
        <p className="text-xs text-yellow-600 mt-1">We found a similar address that might be more accurate:</p>
      </div>
      
      {/* Original Address */}
      <div className="p-3 border-b border-gray-200 bg-red-50">
        <p className="text-sm font-medium text-red-900">Original Address:</p>
        <p className="text-sm text-red-700">{formatOriginalAddress(originalAddress)}</p>
      </div>
      
      {/* Suggested Address */}
      <div 
        className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 bg-green-50"
        onClick={() => onSelect(suggestion)}
      >
        <p className="text-sm font-medium text-green-900">Suggested Address:</p>
        <p className="text-sm text-green-700">{formatSuggestedAddress(suggestion)}</p>
      </div>
      
      <div className="p-2 bg-gray-50 flex justify-end space-x-2">
        <button
          type="button"
          onClick={() => onSelect(suggestion)}
          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Use Suggested
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="px-3 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Keep Original
        </button>
      </div>
    </div>
  );
};

const validation = {
  // Shipping Address
  shippingFullName: { required: true, message: "Full Name is required" },
  shippingEmail: { required: true, message: "Email is required" },
  shippingPhone: { required: true, message: "Phone is required" },
  shippingAddress: { required: true, message: "Address is required" },
  shippingCity: { required: true, message: "City is required" },
  shippingState: { required: true, message: "State is required" },
  shippingZip: { required: true, message: "ZIP Code is required" },
  
  // Billing Address (only validated when isBillingAddressSame is false)
  fullName: { required: true, message: "Billing Full Name is required" },
  email: { required: true, message: "Billing Email is required" },
  phone: { required: true, message: "Billing Phone is required" },
  address: { required: true, message: "Billing Address is required" },
  city: { required: true, message: "Billing City is required" },
  state: { required: true, message: "Billing State is required" },
  zip: { required: true, message: "Billing ZIP Code is required" },
};

const BookCheckout = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeBookId } = useSelector((state) => state.stepFlow);
  const { session } = useSelector((state) => state.authProvider);
  const selectedPlan = state?.selectedPlan;
  
  // Existing state
  const [showStripe, setShowStripe] = useState(false);
  const [, setPaymentIntent] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [isBillingAddressSame, setIsBillingAddressSame] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // New state for address suggestions
  const [shippingSuggestion, setShippingSuggestion] = useState(null);
  const [billingSuggestion, setBillingSuggestion] = useState(null);
  const [shippingOriginalAddress, setShippingOriginalAddress] = useState(null);
  const [billingOriginalAddress, setBillingOriginalAddress] = useState(null);
  const [showShippingSuggestion, setShowShippingSuggestion] = useState(false);
  const [showBillingSuggestion, setShowBillingSuggestion] = useState(false);

  useEffect(() => {
    if (!!session?.user?._id && activeBookId) {
      saveStep({
        userId: session?.user?._id,
        bookId: activeBookId,
        isCompleted: 'yes',
        stepCode: 'Place_Order'
      });
    }
  }, [session?.user?._id, activeBookId]);

  const { values, handleChange, errors, formValidate, resetField, setFieldsValue } = useForm({
    initialState: {
      // Shipping Address (Primary)
      shippingFullName: "",
      shippingEmail: "",
      shippingPhone: "",
      shippingAddress: "",
      shippingCity: "",
      shippingState: "",
      shippingZip: "",
      
      // Billing Address (Secondary)
      fullName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip: "",
    },
    validationRules: validation,
  });

  // Handle shipping address changes and auto-update billing when checkbox is checked
  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    handleChange(e);
    
    // Hide suggestion dropdown when user starts typing
    if (name === 'shippingAddress' || name === 'shippingCity' || name === 'shippingState' || name === 'shippingZip') {
      setShowShippingSuggestion(false);
    }
    
    // If billing address is same as shipping, update corresponding billing field
    if (isBillingAddressSame) {
      const billingFieldMap = {
        shippingFullName: 'fullName',
        shippingEmail: 'email',
        shippingPhone: 'phone',
        shippingAddress: 'address',
        shippingCity: 'city',
        shippingState: 'state',
        shippingZip: 'zip'
      };
      
      const correspondingBillingField = billingFieldMap[name];
      if (correspondingBillingField) {
        setFieldsValue({
          [correspondingBillingField]: value
        });
      }
    }
  };

  // Handle billing address changes
  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    handleChange(e);
    
    // Hide suggestion dropdown when user starts typing
    if (name === 'address' || name === 'city' || name === 'state' || name === 'zip') {
      setShowBillingSuggestion(false);
    }
  };

  // Handle shipping address suggestion selection
  const handleShippingSuggestionSelect = (suggestion) => {
    setFieldsValue({
      shippingFullName: suggestion.name || values.shippingFullName,
      shippingPhone: suggestion.phone || values.shippingPhone,
      shippingAddress: suggestion.address_line1 || '',
      shippingCity: suggestion.city_locality || '',
      shippingState: suggestion.state_province || '',
      shippingZip: suggestion.postal_code || '',
    });

    // If billing address is same as shipping, update billing fields too
    if (isBillingAddressSame) {
      setFieldsValue({
        fullName: suggestion.name || values.shippingFullName,
        phone: suggestion.phone || values.shippingPhone,
        address: suggestion.address_line1 || '',
        city: suggestion.city_locality || '',
        state: suggestion.state_province || '',
        zip: suggestion.postal_code || '',
      });
    }

    setShowShippingSuggestion(false);
    setShippingSuggestion(null);
    setShippingOriginalAddress(null);
  };

  // Handle billing address suggestion selection
  const handleBillingSuggestionSelect = (suggestion) => {
    setFieldsValue({
      fullName: suggestion.name || values.fullName,
      phone: suggestion.phone || values.phone,
      address: suggestion.address_line1 || '',
      city: suggestion.city_locality || '',
      state: suggestion.state_province || '',
      zip: suggestion.postal_code || '',
    });

    setShowBillingSuggestion(false);
    setBillingSuggestion(null);
    setBillingOriginalAddress(null);
  };

  const { mutate: submitOrder, isPending: orderPending } = useMutation({
    mutationFn: async ({ billingAddress, shippingAddress, isBillingAddressSame, planId, bookId }) => {
      const res = await placeOrder({
        billingAddress,
        shippingAddress,
        isBillingAddressSame,
        planId,
        bookId,
      });

      if (res?.status === 200) {
        return res.data;
      } else {
        // Handle 300 status with address suggestions (new format)
        if (res?.status === 300) {
          return { 
            ...res.data, 
            isAddressSuggestion: true,
            status: res.status
          };
        }
        
        toast.error(res?.data?.message || "Failed to submit order.");
        
        if (!!res?.data?.data?.orderId) {
          return res.data;
        }
        
        throw new Error(res?.data?.message || "Failed to submit order.");
      }
    },
    onSuccess: (res) => {
      if (res?.isAddressSuggestion && res?.status === 300) {
        // Handle address suggestion response with new format
        if (res.suggestedAddress && res.originalAddress) {
          // For now, we'll assume it's related to shipping address
          // You might want to add logic to determine if it's shipping or billing
          // based on additional data from your API response
          setShippingSuggestion(res.suggestedAddress);
          setShippingOriginalAddress(res.originalAddress);
          setShowShippingSuggestion(true);
          
          toast.warning(res.message || "Please review the suggested address");
        }
        setIsProcessing(false);
      } else if (res?.status === 200 || !!res?.data?.orderId) {
        setOrderSuccess(true);
        setErrorMessage("");
        setOrderId(res.data?.orderId);
        setShowStripe(true);
      } else {
        setErrorMessage(res?.data?.message || "Failed to submit order.");
        setOrderSuccess(false);
        setShowStripe(false);
        setIsProcessing(false);
      }
    },
    onError: (err) => {
      setErrorMessage(err.message || "Failed to submit order.");
      setOrderSuccess(false);
      setShowStripe(false);
      setIsProcessing(false);
    },
  });

  if (!selectedPlan) return <Navigate to="/" replace />;

  const formatAddress = (prefix = '') => ({
    name: values[`${prefix}fullName`] || values[`${prefix === 'shipping' ? 'shippingFullName' : 'fullName'}`],
    email: values[`${prefix}email`] || values[`${prefix === 'shipping' ? 'shippingEmail' : 'email'}`],
    phone: values[`${prefix}phone`] || values[`${prefix === 'shipping' ? 'shippingPhone' : 'phone'}`],
    address1: values[`${prefix}address`] || values[`${prefix === 'shipping' ? 'shippingAddress' : 'address'}`],
    city: values[`${prefix}city`] || values[`${prefix === 'shipping' ? 'shippingCity' : 'city'}`],
    state: values[`${prefix}state`] || values[`${prefix === 'shipping' ? 'shippingState' : 'state'}`],
    postalCode: values[`${prefix}zip`] || values[`${prefix === 'shipping' ? 'shippingZip' : 'zip'}`],
    country: "US",
  });

  const shippingAddress = formatAddress('shipping');
  const billingAddress = isBillingAddressSame ? shippingAddress : formatAddress();

  const handleVerifyAndCheckout = async () => {
    // Basic validation
    const requiredShippingFields = [
      'shippingFullName', 'shippingEmail', 'shippingPhone', 
      'shippingAddress', 'shippingCity', 'shippingState', 'shippingZip'
    ];

    for (const field of requiredShippingFields) {
      if (!values[field]) {
        toast.error(`${field.replace('shipping', '').replace(/([A-Z])/g, ' $1')} is required`);
        return;
      }
    }

    if (!isBillingAddressSame) {
      const requiredBillingFields = ['fullName', 'email', 'phone', 'address', 'city', 'state', 'zip'];
      for (const field of requiredBillingFields) {
        if (!values[field]) {
          toast.error(`Billing ${field.replace(/([A-Z])/g, ' $1')} is required`);
          return;
        }
      }
    }

    setIsProcessing(true);
    submitOrder({
      billingAddress,
      shippingAddress,
      isBillingAddressSame,
      planId: selectedPlan._id,
      bookId: activeBookId ?? null,
    });
  };

  const handlePaymentSuccess = (intent) => {
    setPaymentIntent(intent);
    resetField();
    setIsProcessing(false);

    dispatch(resetStep());
    dispatch(setCoverImages([]));
    dispatch(setImages([]));

    localStorage.removeItem('selectedPlan');
    navigate("/thank-you", { state: { orderId, paymentIntent: intent } });
  };

  const handleBillingCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setIsBillingAddressSame(isChecked);
    
    if (isChecked) {
      setFieldsValue({
        fullName: values.shippingFullName || '',
        email: values.shippingEmail || '',
        phone: values.shippingPhone || '',
        address: values.shippingAddress || '',
        city: values.shippingCity || '',
        state: values.shippingState || '',
        zip: values.shippingZip || ''
      });
    } else {
      setFieldsValue({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: ''
      });
    }
  };

  const isFieldDisabled = isProcessing || orderPending || orderSuccess;

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded shadow-lg space-y-8">
      {/* Shipping Address Section */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Shipping Address</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            inputName="shippingFullName"
            value={values.shippingFullName}
            onChange={handleShippingChange}
            disabled={isFieldDisabled}
            {...formValidate(errors, "shippingFullName")}
          />
          <Input
            label="Email"
            inputName="shippingEmail"
            value={values.shippingEmail || ""}
            onChange={handleShippingChange}
            disabled={isFieldDisabled}
            {...formValidate(errors, "shippingEmail")}
          />
          <Input
            label="Phone"
            inputName="shippingPhone"
            value={values.shippingPhone || ""}
            onChange={handleShippingChange}
            disabled={isFieldDisabled}
            {...formValidate(errors, "shippingPhone")}
          />
          
          {/* Address field with suggestion dropdown */}
          <div className="relative">
            <Input
              label="Address"
              inputName="shippingAddress"
              value={values.shippingAddress}
              onChange={handleShippingChange}
              disabled={isFieldDisabled}
              {...formValidate(errors, "shippingAddress")}
            />
            <AddressSuggestionDropdown
              suggestion={showShippingSuggestion ? shippingSuggestion : null}
              originalAddress={shippingOriginalAddress}
              onSelect={handleShippingSuggestionSelect}
              onDismiss={() => {
                setShowShippingSuggestion(false);
                setShippingSuggestion(null);
                setShippingOriginalAddress(null);
              }}
            />
          </div>
          
          <Input
            label="City"
            inputName="shippingCity"
            value={values.shippingCity}
            onChange={handleShippingChange}
            disabled={isFieldDisabled}
            {...formValidate(errors, "shippingCity")}
          />
          <Input
            label="State"
            inputName="shippingState"
            value={values.shippingState}
            onChange={handleShippingChange}
            disabled={isFieldDisabled}
            {...formValidate(errors, "shippingState")}
          />
          <Input
            label="ZIP Code"
            inputName="shippingZip"
            value={values.shippingZip}
            onChange={handleShippingChange}
            disabled={isFieldDisabled}
            {...formValidate(errors, "shippingZip")}
          />
        </div>
      </div>

      {/* Billing Address Checkbox */}
      <div className="flex items-center mb-4">
        <input
          type="checkbox"
          id="isBillingAddressSame"
          checked={isBillingAddressSame}
          onChange={handleBillingCheckboxChange}
          disabled={isFieldDisabled}
          className="mr-2 h-4 w-4 text-md-pink focus:ring-md-pink border-gray-300 rounded"
        />
        <label htmlFor="isBillingAddressSame" className="text-lg font-semibold">
          Billing address is the same as the shipping address
        </label>
      </div>

      {/* Billing Address Section */}
      {!isBillingAddressSame && (
        <div>
          <h2 className="text-2xl font-bold mb-4">Billing Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              inputName="fullName"
              value={values.fullName}
              onChange={handleBillingChange}
              disabled={isFieldDisabled}
              {...formValidate(errors, "fullName")}
            />
            <Input
              label="Email"
              inputName="email"
              value={values.email || ""}
              onChange={handleBillingChange}
              disabled={isFieldDisabled}
              {...formValidate(errors, "email")}
            />
            <Input
              label="Phone"
              inputName="phone"
              value={values.phone || ""}
              onChange={handleBillingChange}
              disabled={isFieldDisabled}
              {...formValidate(errors, "phone")}
            />
            
            {/* Billing Address field with suggestion dropdown */}
            <div className="relative">
              <Input
                label="Address"
                inputName="address"
                value={values.address}
                onChange={handleBillingChange}
                disabled={isFieldDisabled}
                {...formValidate(errors, "address")}
              />
              <AddressSuggestionDropdown
                suggestion={showBillingSuggestion ? billingSuggestion : null}
                originalAddress={billingOriginalAddress}
                onSelect={handleBillingSuggestionSelect}
                onDismiss={() => {
                  setShowBillingSuggestion(false);
                  setBillingSuggestion(null);
                  setBillingOriginalAddress(null);
                }}
              />
            </div>
            
            <Input
              label="City"
              inputName="city"
              value={values.city}
              onChange={handleBillingChange}
              disabled={isFieldDisabled}
              {...formValidate(errors, "city")}
            />
            <Input
              label="State"
              inputName="state"
              value={values.state}
              onChange={handleBillingChange}
              disabled={isFieldDisabled}
              {...formValidate(errors, "state")}
            />
            <Input
              label="ZIP Code"
              inputName="zip"
              value={values.zip}
              onChange={handleBillingChange}
              disabled={isFieldDisabled}
              {...formValidate(errors, "zip")}
            />
          </div>
        </div>
      )}

      <div className="flex justify-end">
        {!showStripe && (
          <Button
            buttonLabel={orderPending ? "Submitting..." : "Place Order"}
            buttonFunction={handleVerifyAndCheckout}
            buttonClasses="!rounded-full !px-8 !h-12 !bg-md-pink !text-white"
            {...(orderPending && { isDisable: true, loading: true })}
          />
        )}
      </div>

      {showStripe && orderSuccess && (
        <div>
          <h2 className="text-2xl font-bold mb-4">Payment</h2>
          <PaymentPage
            selectedPlan={selectedPlan}
            orderId={orderId}
            onPaymentSuccess={handlePaymentSuccess}
          />
        </div>
      )}

      {/* Feedback */}
      {orderPending && <p className="text-blue-600">Submitting order...</p>}
      {errorMessage && <p className="text-red-600">{errorMessage}</p>}
      {orderSuccess && !showStripe && (
        <p className="text-green-600">
          Order placed successfully! Please proceed to payment.
        </p>
      )}
    </div>
  );
};

export default BookCheckout;